import { useEffect, useState } from 'react';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import type { PlatformReport } from '../../client/api';
import {
  BoPage,
  Section,
  ErrorBanner,
  btnGhost,
  btnPrimary,
  errMsg,
  formatBytes,
  formatDateTime,
} from './ui';

// =============================================================================
// BO · Operational reports (NBL_REQ_810 / HNBL-118) — platform totals +
// per-tenant rollup with CSV/JSON export links to /api/bo/reports/export.
// =============================================================================

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 rounded border border-line bg-panel-2/50 px-4 py-3">
      <span className="text-[13px] font-medium uppercase tracking-wide text-text-faint">{label}</span>
      <span className="text-[22px] font-semibold leading-none text-text">{value}</span>
    </div>
  );
}

export function Reports(): JSX.Element {
  const [report, setReport] = useState<PlatformReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setReport(await api.boReportOverview());
      setErr(null);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  return (
    <BoPage
      title="운영 리포트"
      subtitle="운영사 전용 — 플랫폼 통계 조회와 CSV/JSON 내보내기."
      actions={
        <>
          <button type="button" className={btnGhost} onClick={() => void load()} disabled={loading}>
            새로고침
          </button>
          <a className={btnGhost} href={api.boReportExportUrl('csv')} target="_blank" rel="noreferrer">CSV 내보내기</a>
          <a className={btnPrimary} href={api.boReportExportUrl('json')} target="_blank" rel="noreferrer">JSON 내보내기</a>
        </>
      }
    >
      <ErrorBanner message={err} />
      {loading && !report && !err ? (
        <ContentSkeleton label="운영 리포트를 불러오는 중…" variant="cards" />
      ) : report ? (
        <>
          <Section title="플랫폼 합계">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="테넌트" value={`${report.totals.tenants} (활성 ${report.totals.activeTenants})`} />
              <Stat label="정지 테넌트" value={String(report.totals.suspendedTenants)} />
              <Stat label="멤버" value={String(report.totals.members)} />
              <Stat label="디바이스" value={`${report.totals.devices} (온라인 ${report.totals.onlineDevices})`} />
              <Stat label="미디어 수" value={String(report.totals.mediaItems)} />
              <Stat label="미디어 용량" value={formatBytes(report.totals.mediaBytes)} />
            </div>
            <p className="mt-3 text-[13px] text-text-faint">생성 시각: {formatDateTime(report.generatedAt)}</p>
          </Section>

          <Section title="테넌트별 통계">
            {report.tenants.length === 0 ? (
              <p className="py-6 text-center text-[14px] text-text-faint">테넌트가 없습니다.</p>
            ) : (
              <div className="overflow-x-auto rounded border border-line">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="border-b border-line bg-panel-2">
                      <th className="px-3 py-2 text-left font-semibold text-text-dim">조직</th>
                      <th className="px-3 py-2 text-left font-semibold text-text-dim">상태</th>
                      <th className="px-3 py-2 text-right font-semibold text-text-dim">멤버</th>
                      <th className="px-3 py-2 text-right font-semibold text-text-dim">디바이스</th>
                      <th className="px-3 py-2 text-right font-semibold text-text-dim">온라인</th>
                      <th className="px-3 py-2 text-right font-semibold text-text-dim">미디어</th>
                      <th className="px-3 py-2 text-right font-semibold text-text-dim">용량</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.tenants.map((t) => (
                      <tr key={t.orgId} className="border-b border-line last:border-0 hover:bg-panel-hover transition-colors">
                        <td className="px-3 py-2">
                          <div className="text-text">{t.orgName}</div>
                          <div className="font-mono text-[12px] text-text-faint">{t.orgId}</div>
                        </td>
                        <td className="px-3 py-2 text-text-dim">{t.status === 'suspended' ? '정지' : '활성'}</td>
                        <td className="px-3 py-2 text-right text-text-dim">{t.memberCount}</td>
                        <td className="px-3 py-2 text-right text-text-dim">{t.deviceCount}</td>
                        <td className="px-3 py-2 text-right text-text-dim">{t.onlineDeviceCount}</td>
                        <td className="px-3 py-2 text-right text-text-dim">{t.mediaCount}</td>
                        <td className="px-3 py-2 text-right text-text-dim">{formatBytes(t.mediaBytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      ) : null}
    </BoPage>
  );
}
